import clsx from "clsx";
import { useState } from "react";

// Interfaces/Types
import { IUser } from "@/Common/interfaces/user";
import { IAccessPositions, ISelected, IWashoutData, IWashoutQueries } from "@/Washouts/types/WashoutReview";

// Hooks
import { useWashoutReviewActions } from "@/Washouts/hooks/WashoutReview/useWashoutReviewActions";
import { useChangeRequestsData } from "@/Washouts/hooks/ChangeRequests/useChangeRequestsData";
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";

// Helpers
import { checkEditablePosition } from "@/Washouts/helpers/WashoutReview";
import { or } from "@/Common/helpers/sonar";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";

// MUI
import { Grid, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";

// Common Components
import { ChangeRequestContainer } from "@/Washouts/components/ChangeRequestContainer";
import ChangeRequestPositionedPopper from "@/Washouts/components/ChangeRequestPositionedPopper";

type TChangeRequestSectionProps = {
	washoutData?: IWashoutData;
	accessPositions: IAccessPositions;
	isApproved: boolean;
	user: IUser | null;
	position: string;
	washoutQueries: IWashoutQueries;
	locationId: string;
	currentCommissionCycleId: string;
	selected: ISelected | null;
};

export const ChangeRequestSection = (props: TChangeRequestSectionProps) => {
	const { washoutData, accessPositions, isApproved, user, washoutQueries, position, locationId, currentCommissionCycleId, selected } =
		props;

	const { userId, payPeriodId, commissionCycleId: selectedCycleId = "" } = selected ?? { userId: "", position: "", payPeriodId: "" };
	const commissionCycleId = selectedCycleId ?? currentCommissionCycleId;
	const origin = "REVIEW";

	const { data: changeRequestData, isLoading } = useChangeRequestsData({
		locationId,
		commissionCycleId,
		filter: {
			userId,
			origin,
			position,
			payPeriodId,
		},
	});

	const [openCreateChangeRequestPopper, setOpenCreateChangeRequestPopper] = useState<boolean>(false);

	const {
		createChangeRequestMutation,
		isPendingCreateChangeRequest,
		updateWashoutStatusMutation,
		isPendingUpdateWashout,
		generateWashoutMutation,
		isPendingGenerateWashout,
	} = useWashoutReviewActions({
		locationId,
		commissionCycleId,
		payPeriodId,
		washoutQueries,
		position,
		userId,
	});

	const handleApproveWashout = () => {
		if (!!locationId && !!washoutData?.commissionCycleId && !!washoutData?.payPeriodId && !!washoutData?.employee?._id) {
			const input = {
				locationId,
				commissionCycleId: washoutData.commissionCycleId,
				payPeriodId: washoutData.payPeriodId,
				userId: washoutData.employee._id,
				status: {
					status: washoutData?.review?.status === "APPROVED" ? "RETRACTED" : "APPROVED",
					statusSetBy: user?._id,
					statusSetOn: new Date(),
				},
			};
			updateWashoutStatusMutation(input);
		}
	};

	const handleSaveRequest = (issueSummary: string, description: string) => {
		const input = {
			issueSummary,
			description,
			origin,
			position,
		};
		createChangeRequestMutation(input);
	};

	const handleGenerateWashout = () => {
		generateWashoutMutation();
	};

	const { positionStatus } = useCommissionCyclesData({ commissionCycleId, payPeriodId });

	const currentPositionStatus = positionStatus?.find((status) => status.position === position);

	const isPendingAction = or(isLoading, isPendingUpdateWashout);
	const isPositionApproved = currentPositionStatus?.status === "APPROVED";
	const isPostedToPayroll = currentPositionStatus?.status === "POSTED_PAYROLL";
	const hasPendingRequests = Boolean(changeRequestData?.requests?.some((entry) => entry.status === "PENDING"));
	const hasNoAccess = !checkEditablePosition(washoutData, accessPositions);
	const isNotApproved = (washoutData as { review: { status: string } })?.review?.status !== "APPROVED";
	const isMandatoryDisabled = or(isPendingAction, isPostedToPayroll, hasNoAccess);
	const isDisabledApprove = or(isMandatoryDisabled, hasPendingRequests);
	const isButtonDisabled = isNotApproved ? isDisabledApprove : isMandatoryDisabled;
	const approvalButtonText = isNotApproved ? "Approve Washout" : "Retract Approval";
	const generateButtonText = isPendingGenerateWashout ? null : "Generate Washout";

	const shouldDisableGenerateButton = or(isPendingGenerateWashout, isPositionApproved, isPostedToPayroll);

	return (
		<>
			{washoutData && (
				<>
					<Grid item sx={{ paddingTop: "20px" }}>
						<Grid container direction="row" justifyContent="space-between">
							<Grid item px={"30px"}>
								<Typography className="page-title" component="div">
									Change Requests
								</Typography>
							</Grid>
							<Grid item sx={{ paddingRight: "20px" }}>
								<Grid container direction="row" justifyContent="space-between" spacing={4}>
									<Grid item mr={4}>
										<LoadingButton
											variant="outlined"
											loadingPosition="center"
											loading={isPendingGenerateWashout}
											disabled={shouldDisableGenerateButton}
											onClick={handleGenerateWashout}
											disableElevation
											classes={{
												loadingIndicator: "!text-white",
												root: clsx({
													"!h-[30px] !w-[127px] !font-sans !capitalize !text-primary !text-[14px] !rounded-[6px]": true,
													"!border-primary hover:!shadow-primary-shadow-50": !shouldDisableGenerateButton,
													"!bg-disabled !border-disabled !text-white !cursor-default":
														shouldDisableGenerateButton,
												}),
											}}
										>
											{generateButtonText}
										</LoadingButton>
									</Grid>
									<Grid item>
										<ChangeRequestPositionedPopper
											disabled={isApproved}
											handleSaveRequest={handleSaveRequest}
											open={openCreateChangeRequestPopper}
											onOpen={setOpenCreateChangeRequestPopper}
										/>
									</Grid>
									<Grid item>
										<Button
											variant="contained"
											aria-controls="select-plan-dialog-assign-btn-menu"
											disabledElevation
											classes={clsx({
												"!h-[30px] !font-sans !capitalize !text-white !text-[14px] !rounded-[6px]": true,
												"!bg-primary hover:!shadow-primary-shadow-50": !isButtonDisabled,
												"!bg-disabled !cursor-default": isButtonDisabled,
												"!pr-[40px]": isPendingUpdateWashout,
											})}
											text={approvalButtonText}
											loading={isPendingUpdateWashout}
											disabled={isButtonDisabled}
											onClick={handleApproveWashout}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item container>
						<Grid
							item
							xs={12}
							sx={{
								paddingTop: washoutData ? "20px" : "0px",
								paddingLeft: "30px",
								paddingBottom: "40px",
								position: "relative",
								marginRight: "20px",
							}}
						>
							<ChangeRequestContainer
								payPeriodId={payPeriodId}
								locationId={locationId}
								commissionCycleId={commissionCycleId}
								origin={origin}
								userId={userId}
								position={position}
								skeletonRows={isPendingCreateChangeRequest ? [""] : []}
								tableProps={{
									allowDeletion: true,
									switchValue: "PENDING",
								}}
								disableRowClick={openCreateChangeRequestPopper}
								hideOpenWashout={true}
								statusActionTooltipText={"Resolve from Support Center"}
								canEditWashout={true}
							/>
						</Grid>
					</Grid>
				</>
			)}
		</>
	);
};

export default ChangeRequestSection;
